import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { defaultToastOptions } from "../../../../../utils/misc";
import { useCreateUserNote } from "../../../../../apis/userApi.hooks";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
} from "@pushpress/pushpress-ui";
type AddNotesProps = {
  userUuid: string;
  showAddNotes: boolean;
  setShowAddNotes: (bool: boolean) => void;
};

type AddNoteForm = {
  note: string;
  highPriority: boolean;
};

export default function AddNotesModal({
  userUuid,
  showAddNotes,
  setShowAddNotes,
}: AddNotesProps) {
  const {
    handleSubmit,
    register,
    setError,
    clearErrors,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<AddNoteForm>({
    mode: "onTouched",
    defaultValues: {
      note: "",
      highPriority: false,
    },
  });

  const { mutate, isLoading: isNoteSaving } = useCreateUserNote();
  const handleSave = (data: AddNoteForm) => {
    const { note } = data;
    let hasError = false;

    if (note === "") {
      setError("note", { message: "Required" });
      hasError = true;
    }

    if (hasError) return;
    mutate(
      { userUuid, body: data },
      {
        onSuccess: () => {
          toast.success("Added note successfully", defaultToastOptions);
          closeModal();
        },
        onError: () => {
          toast.error(
            "There was an error adding the note",
            defaultToastOptions,
          );
        },
      },
    );
  };

  const closeModal = () => {
    reset();
    clearErrors();
    setShowAddNotes(false);
  };
  return (
    <Dialog open={showAddNotes} onOpenChange={closeModal}>
      <DialogContent
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Add Note to User</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(handleSave)}>
          <div className="tw-space-y-4 tw-pb-4">
            <div className="tw-flex tw-flex-col tw-gap-1">
              <Label htmlFor="note" className="tw-text-left">
                Note
              </Label>
              <Textarea
                {...register("note")}
                id="note"
                placeholder="Enter your note here"
                className={`
          tw-min-h-[100px] tw-resize-none tw-w-full
          ${errors.note ? "tw-border-red-500 tw-focus-visible:tw-ring-error" : ""}
        `}
              />
              {errors.note && (
                <div className="tw-text-xs tw-text-error tw-mt-1">
                  {errors.note.message}
                </div>
              )}
            </div>

            <div className="tw-flex tw-items-center tw-gap-2">
              <Checkbox
                {...register("highPriority")}
                onCheckedChange={(checked: boolean) => {
                  setValue("highPriority", checked);
                }}
                checked={watch("highPriority")}
                id="high-priority"
              />
              <Label htmlFor="high-priority" className="tw-mb-0">
                High Priority
              </Label>
            </div>
          </div>
          <DialogFooter className="tw-mt-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={closeModal}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              size="sm"
              isSubmitting={isNoteSaving}
              disabled={isNoteSaving}
            >
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
